import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from ".././Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
// Lazy load shared chart components
const SharedPieChart = React.lazy(() => import("../../Sharedpiechart"));
const SharedBarChart = React.lazy(() => import("../../Sharedbarchart"));
const TITLE =
  "حاسبة نسبة الغياب - حساب نسبة الغياب بناءً على عدد الحضور والغياب";
const DESCRIPTION =
  "حاسبة نسبة الغياب تقوم بحساب نسبة الغياب بناءً على عدد الحضور والغياب الكليين.";
const KEYWORDS = "نسبة الغياب, حساب نسبة الغياب, حاسبة نسبة الغياب";
const OG_URL = "https://do-calculate.com/calculator/ar/absence-percentage";
function Calculator() {
  // Main Calculator State
  const [calculationMode, setCalculationMode] = useState("attendance-absence");
  const [totalAttendees, setTotalAttendees] = useState("");
  const [totalAbsences, setTotalAbsences] = useState("");
  const [totalCount, setTotalCount] = useState("");
  const [attendanceOnly, setAttendanceOnly] = useState("");
  const [absencePercentage, setAbsencePercentage] = useState("");
  const [attendancePercentage, setAttendancePercentage] = useState("");
  const [difference, setDifference] = useState("");
  const [error, setError] = useState("");
  const [showChart, setShowChart] = useState(false);
  // Weekly/Monthly Calculator State - Start with 2 items by default
  const [period, setPeriod] = useState("weekly");
  const [weeklyData, setWeeklyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [monthlyData, setMonthlyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [periodResult, setPeriodResult] = useState(null);
  const [periodError, setPeriodError] = useState("");
  const [showPeriodChart, setShowPeriodChart] = useState(false);
  const handleCalculate = () => {
    if (calculationMode === "attendance-absence") {
      if (!totalAttendees || !totalAbsences) {
        setError("الرجاء إدخال عدد الحضور وعدد الغياب بشكل صحيح");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const attendeesValue = parseInt(totalAttendees);
      const absencesValue = parseInt(totalAbsences);
      if (
        isNaN(attendeesValue) ||
        isNaN(absencesValue) ||
        attendeesValue < 0 ||
        absencesValue < 0
      ) {
        setError("الرجاء إدخال قيم صحيحة وغير سالبة لعدد الحضور وعدد الغياب");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const totalValue = attendeesValue + absencesValue;
      const absPercentage = (absencesValue / totalValue) * 100;
      const attPercentage = (attendeesValue / totalValue) * 100;
      const diff = attPercentage - absPercentage;
      setAbsencePercentage(absPercentage.toFixed(2));
      setAttendancePercentage(attPercentage.toFixed(2));
      setDifference(diff.toFixed(2));
      setError("");
      setShowChart(true);
    } else {
      if (!totalCount || !attendanceOnly) {
        setError("الرجاء إدخال العدد الكلي وعدد الحضور بشكل صحيح");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const total = parseInt(totalCount);
      const attendance = parseInt(attendanceOnly);
      if (
        isNaN(total) ||
        isNaN(attendance) ||
        total < 0 ||
        attendance < 0 ||
        attendance > total
      ) {
        setError("الرجاء إدخال قيم صحيحة (عدد الحضور يجب أن يكون أقل من أو يساوي العدد الكلي)");
        setAbsencePercentage("");
        setShowChart(false);
        return;
      }
      const absenceValue = total - attendance;
      const absPercentage = (absenceValue / total) * 100;
      const attPercentage = (attendance / total) * 100;
      const diff = attPercentage - absPercentage;
      setAbsencePercentage(absPercentage.toFixed(2));
      setAttendancePercentage(attPercentage.toFixed(2));
      setDifference(diff.toFixed(2));
      setError("");
      setShowChart(true);
    }
  };
  const handlePeriodCalculate = () => {
    let results = [];
    let totalAtt = 0;
    let totalAbs = 0;
    const dataMap = { weekly: weeklyData, monthly: monthlyData, daily: dailyData, yearly: yearlyData };
    const nameMap = { weekly: "الأسبوع", monthly: "الشهر", daily: "اليوم", yearly: "السنة" };
    const data = dataMap[period];
    const periodName = nameMap[period];

    for (let i = 0; i < data.length; i++) {
      const att = parseInt(data[i].attendance) || 0;
      const abs = parseInt(data[i].absence) || 0;
      if (att < 0 || abs < 0) {
        setPeriodError("الرجاء إدخال قيم صحيحة وغير سالبة");
        setPeriodResult(null);
        setShowPeriodChart(false);
        return;
      }
      const total = att + abs;
      const absPercent = total > 0 ? ((abs / total) * 100).toFixed(2) : "0.00";
      const attPercent = total > 0 ? ((att / total) * 100).toFixed(2) : "0.00";
      results.push({
        name: `${periodName} ${i + 1}`,
        attendance: att,
        absence: abs,
        total: total,
        absencePercentage: absPercent,
        attendancePercentage: attPercent,
      });
      totalAtt += att;
      totalAbs += abs;
    }
    const overallTotal = totalAtt + totalAbs;
    const overallAbsPercent =
      overallTotal > 0 ? ((totalAbs / overallTotal) * 100).toFixed(2) : "0.00";
    const overallAttPercent =
      overallTotal > 0 ? ((totalAtt / overallTotal) * 100).toFixed(2) : "0.00";
    setPeriodResult({
      periods: results,
      overall: {
        totalAttendance: totalAtt,
        totalAbsence: totalAbs,
        total: overallTotal,
        absencePercentage: overallAbsPercent,
        attendancePercentage: overallAttPercent,
      },
    });
    setPeriodError("");
    setShowPeriodChart(true);
  };
  const updateWeeklyData = (index, field, value) => {
    const newData = [...weeklyData];
    newData[index][field] = value;
    setWeeklyData(newData);
  };
  const updateMonthlyData = (index, field, value) => {
    const newData = [...monthlyData];
    newData[index][field] = value;
    setMonthlyData(newData);
  };
  const addWeek = () => {
    setWeeklyData([
      ...weeklyData,
      { id: weeklyData.length + 1, attendance: "", absence: "" },
    ]);
  };
  const [dailyData, setDailyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const [yearlyData, setYearlyData] = useState([
    { id: 1, attendance: "", absence: "" },
    { id: 2, attendance: "", absence: "" },
  ]);
  const addDay = () => {
    setDailyData([...dailyData, { id: dailyData.length + 1, attendance: "", absence: "" }]);
  };
  const removeDay = (index) => {
    if (dailyData.length > 1) {
      setDailyData(dailyData.filter((_, i) => i !== index));
      setPeriodResult(null); setShowPeriodChart(false);
    }
  };
  const updateDailyData = (index, field, value) => {
    const newData = [...dailyData];
    newData[index][field] = value;
    setDailyData(newData);
  };
  const addYear = () => {
    setYearlyData([...yearlyData, { id: yearlyData.length + 1, attendance: "", absence: "" }]);
  };
  const removeYear = (index) => {
    if (yearlyData.length > 1) {
      setYearlyData(yearlyData.filter((_, i) => i !== index));
      setPeriodResult(null); setShowPeriodChart(false);
    }
  };
  const updateYearlyData = (index, field, value) => {
    const newData = [...yearlyData];
    newData[index][field] = value;
    setYearlyData(newData);
  };
  const removeWeek = (index) => {
    if (weeklyData.length > 1) {
      const newData = weeklyData.filter((_, i) => i !== index);
      setWeeklyData(newData);
      setPeriodResult(null);
      setShowPeriodChart(false);
    }
  };
  const addMonth = () => {
    setMonthlyData([
      ...monthlyData,
      { id: monthlyData.length + 1, attendance: "", absence: "" },
    ]);
  };
  const removeMonth = (index) => {
    if (monthlyData.length > 1) {
      const newData = monthlyData.filter((_, i) => i !== index);
      setMonthlyData(newData);
      setPeriodResult(null);
      setShowPeriodChart(false);
    }
  };
  return (
    <div className="rtl">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <h2 style={{ fontSize: "18px" }}>طريقة حساب نسبة الغياب </h2>
                <p>
                  لحساب نسبة الغياب يجب حساب عدد الحضور بالكامل ومعرفة عدد
                  الأشخاص بالكامل بالحضور والغياب، ثم نقوم بقسمة عدد الغياب على
                  العدد الإجمالي وبعدها نضرب في 100% للحصول على نسبة الغياب.
                </p>
                <p>
                  نسبة الغياب = (عدد الغياب / (عدد الحضور + عدد الغياب)) * 100%
                </p>
                <p>
                  يمكن استخدام هذه الحاسبة لحساب نسبة الغياب بناءً على الأرقام
                  المُدخلة. توفر الحاسبة خيارين للإدخال: إما بإدخال عدد الحضور والغياب مباشرة، أو بإدخال العدد الكلي وعدد الحضور فقط. كما تعرض الحاسبة رسماً بيانياً دائرياً يوضح نسبة الحضور والغياب بشكل مرئي، بالإضافة إلى حساب الفرق بين نسبة الحضور ونسبة الغياب لمعرفة أيهما أعلى.
                </p>
                <p>
                  كما يمكنك استخدام حاسبة نسبة الغياب الأسبوعية والشهرية لتتبع نسب الحضور والغياب على مدار عدة أسابيع أو أشهر، مع إمكانية إضافة أو حذف أسابيع وأشهر حسب الحاجة. تعرض النتائج في جدول مفصل مع رسم بياني عمودي واضح يوضح المقارنة بين نسب الحضور والغياب لكل فترة زمنية.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            {/* Main Calculator */}
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE} </h2>
                </div>
                <hr />
                <div dir="rtl">
                  <div className="mb-3">
                    <label>
                      <b>طريقة الحساب:</b>
                    </label>
                    <br />
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="calculationMode"
                        id="mode1"
                        value="attendance-absence"
                        checked={calculationMode === "attendance-absence"}
                        onChange={(e) => {
                          setCalculationMode(e.target.value);
                          setAbsencePercentage("");
                          setError("");
                          setShowChart(false);
                        }}
                      />
                      <label className="form-check-label" htmlFor="mode1">
                        إدخال عدد الحضور وعدد الغياب
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="calculationMode"
                        id="mode2"
                        value="total-attendance"
                        checked={calculationMode === "total-attendance"}
                        onChange={(e) => {
                          setCalculationMode(e.target.value);
                          setAbsencePercentage("");
                          setError("");
                          setShowChart(false);
                        }}
                      />
                      <label className="form-check-label" htmlFor="mode2">
                        إدخال العدد الكلي وعدد الحضور فقط
                      </label>
                    </div>
                  </div>
                  <div>
                    {calculationMode === "attendance-absence" ? (
                      <>
                        <div className="form-group d-flex align-items-center">
                          <b>
                            <label className="form-label ms-2">
                              عدد الحضور
                            </label>
                          </b>
                          <input
                            type="number"
                            placeholder="عدد الحضور"
                            value={totalAttendees}
                            onChange={(e) => setTotalAttendees(e.target.value)}
                            className="form-control"
                            style={{ width: "90%" }}
                          />
                        </div>
                        <br />
                        <div className="form-group d-flex align-items-center">
                          <b>
                            <label className="form-label ms-2">
                              عدد الغياب
                            </label>
                          </b>
                          <input
                            type="number"
                            placeholder="عدد الغياب"
                            value={totalAbsences}
                            onChange={(e) => setTotalAbsences(e.target.value)}
                            className="form-control"
                            style={{ width: "90%" }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group d-flex align-items-center">
                          <b>
                            <label className="form-label ms-2">
                              العدد الكلي
                            </label>
                          </b>
                          <input
                            type="number"
                            placeholder="العدد الكلي"
                            value={totalCount}
                            onChange={(e) => setTotalCount(e.target.value)}
                            className="form-control"
                            style={{ width: "90%" }}
                          />
                        </div>
                        <br />
                        <div className="form-group d-flex align-items-center">
                          <b>
                            <label className="form-label ms-2">
                              عدد الحضور
                            </label>
                          </b>
                          <input
                            type="number"
                            placeholder="عدد الحضور"
                            value={attendanceOnly}
                            onChange={(e) => setAttendanceOnly(e.target.value)}
                            className="form-control"
                            style={{ width: "90%" }}
                          />
                        </div>
                      </>
                    )}
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px" }}
                      onClick={handleCalculate}
                    >
                      احسب نسبة الغياب
                    </button>
                  </div>
                  <br />
                  <div>
                    {error !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">
                        خطأ: {error}
                      </p>
                    ) : (
                      <>
                        {absencePercentage !== "" && (
                          <>
                            <p className="p-3 mb-2 bg-success text-white">
                              نسبة الغياب: {absencePercentage}%
                            </p>
                            <p className="p-3 mb-2 bg-success text-white">
                              نسبة الحضور: {attendancePercentage}%
                            </p>
                            <p className="p-3 mb-2 bg-success text-white">
                              الفرق: {Math.abs(parseFloat(difference))}%{" "}
                              {parseFloat(difference) > 0
                                ? "(الحضور أعلى)"
                                : parseFloat(difference) < 0
                                  ? "(الغياب أعلى)"
                                  : "(متساوي)"}
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-sm-12 col-md-5">
                {showChart && absencePercentage && (
                  <React.Suspense
                    fallback={
                      <div className="text-center p-3">
                        جاري تحميل الرسم البياني...
                      </div>
                    }
                  >
                    <SharedPieChart
                      firstLabel="نسبة الحضور"
                      secondLabel="نسبة الغياب"
                      firstValue={attendancePercentage}
                      secondValue={absencePercentage}
                      firstColor="#28a745"
                      secondColor="#dc3545"
                      title="الجدول البياني"
                    />
                  </React.Suspense>
                )}
              </div>
            </div>
            <hr />
            {/* Weekly/Monthly Calculator */}
            <div className="row">
              <div className="col-sm-12 col-md-5 calculatorStyle">

                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>
                    حاسبة نسبة الغياب الأسبوعية والشهرية
                  </h2>
                </div>
                <hr />
                <div dir="rtl">
                  <div className="mb-3">
                    <label className="ms-2">
                      <b>اختر الفترة:</b>
                    </label>
                    <select
                      className="form-select"
                      style={{ width: "200px", display: "inline-block" }}
                      value={period}
                      onChange={(e) => {
                        setPeriod(e.target.value);
                        setPeriodResult(null);
                        setShowPeriodChart(false);
                      }}
                    >
                      <option value="daily">يومي</option>
                      <option value="weekly">أسبوعي</option>
                      <option value="monthly">شهري</option>
                      <option value="yearly">سنوي</option>
                    </select>
                  </div>
                {(period === "daily" ? dailyData : period === "yearly" ? yearlyData : period === "weekly" ? weeklyData : monthlyData).map((item, index) => {
                  const label = { daily: "اليوم", weekly: "الأسبوع", monthly: "الشهر", yearly: "السنة" }[period];
                  const updateFn = { daily: updateDailyData, weekly: updateWeeklyData, monthly: updateMonthlyData, yearly: updateYearlyData }[period];
                  const removeFn = { daily: removeDay, weekly: removeWeek, monthly: removeMonth, yearly: removeYear }[period];
                  const currentData = { daily: dailyData, weekly: weeklyData, monthly: monthlyData, yearly: yearlyData }[period];
                  return (
                    <div key={item.id} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h3 style={{ fontSize: "15px" }}><b>{label} {index + 1}</b></h3>
                        {currentData.length > 1 && (
                          <button className="btn btn-sm btn-secondary" onClick={() => removeFn(index)}>حذف</button>
                        )}
                      </div>
                      <div className="d-flex gap-2 align-items-center mb-2">
                        <label style={{ minWidth: "80px" }}>عدد الحضور:</label>
                        <input type="number" className="form-control" style={{ width: "120px" }} value={item.attendance}
                          onChange={(e) => updateFn(index, "attendance", e.target.value)} />
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <label style={{ minWidth: "80px" }}>عدد الغياب:</label>
                        <input type="number" className="form-control" style={{ width: "120px" }} value={item.absence}
                          onChange={(e) => updateFn(index, "absence", e.target.value)} />
                      </div>
                    </div>
                  );
                })}
                <button className="btn btn-sm btn-secondary mb-3" onClick={{ daily: addDay, weekly: addWeek, monthly: addMonth, yearly: addYear }[period]}>
                  + إضافة {{ daily: "يوم", weekly: "أسبوع", monthly: "شهر", yearly: "سنة" }[period]}
                </button>
                <br />
                <button
                  className="btn btn-success"
                  style={{ fontSize: "20px" }}
                  onClick={handlePeriodCalculate}
                >
                  احسب النسب
                </button>
                <br />
                <div>
                  {periodError !== "" ? (
                    <p className="p-3 mb-2 bg-warning text-white">
                      خطأ: {periodError}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            {periodResult && (
              <div className="col-sm-12 col-md-7 calculatorStyle">

                <h2 className="p-3 mb-2 bg-success text-white" style={{ fontSize: "16px" }}>
                  النتائج
                </h2>
                <div style={{ overflowX: "auto", width: "100%" }}>
                  <table style={{ fontSize: "12px", width: "100%" }}>
                    <thead>
                      <tr>
                        <th >لفترة</th>
                        <th >حضور</th>
                        <th>غياب</th>
                        <th >مجموع</th>
                        <th > غياب</th>
                        <th > حضور</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periodResult.periods.map((p, index) => (
                        <tr key={index}>
                          <td>{p.name}</td>
                          <td>{p.attendance}</td>
                          <td>{p.absence}</td>
                          <td>{p.total}</td>
                          <td>{p.absencePercentage}%</td>
                          <td>{p.attendancePercentage}%</td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                        <td>الإجمالي</td>
                        <td>{periodResult.overall.totalAttendance}</td>
                        <td>{periodResult.overall.totalAbsence}</td>
                        <td>{periodResult.overall.total}</td>
                        <td>{periodResult.overall.absencePercentage}%</td>
                        <td>{periodResult.overall.attendancePercentage}%</td>
                      </tr>
                    </tbody>
                  </table>
                  {showPeriodChart && (
                    <React.Suspense
                      fallback={
                        <div className="text-center p-3">
                          جاري تحميل الرسم البياني...
                        </div>
                      }
                    >
                      <SharedBarChart
                        periodResult={periodResult}
                        firstLabel="نسبة الحضور %"
                        secondLabel="نسبة الغياب %"
                        firstColor="rgba(40, 167, 69, 0.8)"
                        secondColor="rgba(220, 53, 69, 0.8)"
                        firstBorderColor="rgba(40, 167, 69, 1)"
                        secondBorderColor="rgba(220, 53, 69, 1)"
                        title="الرسم البياني للنسب"
                      />
                    </React.Suspense>
                  )}
                </div>
              </div>
            )}

          </div>
          <hr />
          <div className="row">
            <RelatedLinks />
            <hr />
          </div>
        </div>
      </div>
      <div className="col">
        <SecondAds />
        <br />
        <MainCalculatorsList></MainCalculatorsList>
        <br />
      </div>
    </div>
    </div >
  );
}
export default Calculator;